
import type { PropType } from 'vue'
import type { PivotableAudit } from '../../../../composables/use-pivotable-audit'
import type {
  AuditItem,
  AuditMessages,
  ConfigurationsMessages,
  EqualizeMessages,
} from '../../../../models'
import type {
  Equalize,
  EqualizeBoostingField,
  EqualizeFunctionField,
} from '../../../../modules/equalize'
import type { AuditItemDetailsMessages } from './audit-item-details.component.vue'
import { computed, defineComponent, getCurrentInstance } from 'vue'
import { usePivotableAudit } from '../../../../composables/use-pivotable-audit'
import AuditItemDetails from './audit-item-details.component.vue'

type AuditEqualize = Pick<Equalize, 'id' | 'metadata' | 'title' | 'notes'> & {
  criteria: PivotableAudit & { enabled: string }
} & {
  features: {
    multiMatchSearch: Record<string, number>
    semanticMatchSearch: Record<string, number>
    boosting: Array<EqualizeBoostingField>
    functionScore: Array<EqualizeFunctionField>
    sponsoredScore: Array<EqualizeFunctionField>
  }
}

export default defineComponent({
  name: 'EqualizeAuditItemDetails',
  components: {
    AuditItemDetails,
  },
  props: {
    item: {
      type: Object as PropType<AuditItem<Equalize>>,
      required: true,
    },
    messages: {
      type: Object as PropType<AuditMessages['entry']>,
      required: true,
    },
  },
  setup(props) {
    const {
      proxy: { $epb },
    } = getCurrentInstance()

    const { transform: transformPivots } = usePivotableAudit()

    const toolMessages: ConfigurationsMessages = $epb.messages.tools.configurations

    function transformState(state: Equalize): AuditEqualize {
      const { id, metadata, criteria, title, notes, features } = state
      const multiMatchSearch = features.multiMatchSearch.config.searchableFields.reduce(
        (result, field) => ({ ...result, [field.name]: field.boost }),
        {},
      )
      const semanticMatchSearch = features.semanticMatchSearch.config.searchableFields.reduce(
        (result, field) => ({ ...result, [field.name]: field.boost }),
        {},
      )
      const boosting = features.boosting.config.boosts.map(field => ({
        field: field.field,
        value: field.value,
        action: field.action,
        boostFactor: field.boostFactor,
      }))
      const functionScore = features.functionScore.config.functions.map(field => ({
        fieldName: field.fieldName,
        formulaType: field.formulaType,
        weight: field.weight,
        boostFactor: field.boostFactor,
      }))
      const sponsoredScore = features.sponsoredScore.config.functions.map(field => ({
        fieldName: field.fieldName,
        formulaType: field.formulaType,
        weight: field.weight,
        boostFactor: field.boostFactor,
      }))
      return {
        id,
        metadata,
        criteria: {
          ...criteria,
          enabled: state.criteria.enabled
            ? toolMessages.fields.enabled.states.enabled
            : toolMessages.fields.enabled.states.disabled,
          ...transformPivots(criteria),
        },
        title,
        notes,
        features: {
          multiMatchSearch,
          semanticMatchSearch,
          boosting,
          functionScore,
          sponsoredScore,
        },
      }
    }
    const transformedItem = computed<AuditItem<AuditEqualize>>(() => {
      const { previousState, nextState } = props.item.payload
      return {
        ...props.item,
        payload: {
          ...props.item.payload,
          previousState: previousState ? transformState(previousState) : undefined,
          nextState: nextState ? transformState(nextState) : undefined,
        },
      }
    })

    const transformedMessages = computed<AuditItemDetailsMessages>(() => {
      const toolMessages = $epb.messages.tools.equalize as EqualizeMessages
      return {
        previousState: props.messages.previousState,
        nextState: props.messages.nextState,
        fields: {
          created: toolMessages.fields.creationDate.label,
          updated: toolMessages.fields.updateDate.label,
          author: toolMessages.fields.user.label,
          enabled: toolMessages.fields.enabled.label,
          title: toolMessages.fields.title.label,
          notes: toolMessages.fields.notes.label,
          multiMatchSearch: toolMessages.features.multiMatchSearch.title,
          semanticMatchSearch: toolMessages.features.semanticMatchSearch.title,
          boosting: toolMessages.features.boosting.title,
          'boosting.field': toolMessages.features.boosting.audit.field,
          'boosting.value': toolMessages.features.boosting.audit.value,
          'boosting.action': toolMessages.features.boosting.audit.action,
          'boosting.boostFactor': toolMessages.features.boosting.audit.boostFactor,
          functionScore: toolMessages.features.functionScore.title,
          'functionScore.fieldName': toolMessages.features.functionScore.audit.fieldName,
          'functionScore.formulaType': toolMessages.features.functionScore.audit.formulaType,
          'functionScore.weight': toolMessages.features.functionScore.audit.weight,
          'functionScore.boostFactor': toolMessages.features.functionScore.audit.boostFactor,
          sponsoredScore: toolMessages.features.sponsoredScore.title,
          'sponsoredScore.fieldName': toolMessages.features.sponsoredScore.audit.fieldName,
          'sponsoredScore.formulaType': toolMessages.features.sponsoredScore.audit.formulaType,
          'sponsoredScore.weight': toolMessages.features.sponsoredScore.audit.weight,
          'sponsoredScore.boostFactor': toolMessages.features.sponsoredScore.audit.boostFactor,
        },
      }
    })

    return {
      transformedItem,
      transformedMessages,
    }
  },
})
